import { useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '@shared/api';
import { clearQueryResponseCache } from '@shared/lib/query-helpers';
import { userProfileKeys } from '@entities/user-profile';
import { refreshPermissionsAfterRoleSave, useAuth } from '@features/auth';

import { adminUserRoleAssignmentsQuery, adminUsersSummaryQuery, adminUsersKeys } from './queries';
import {
  adminUserDeletionReceiptSchema,
  adminUserDeletionStatusSchema,
  updateAdminUserRoleAssignmentsResponseSchema,
  type UpdateAdminUserRoleAssignmentsInput,
} from './schemas';

export function useAdminUsersSummary(enabled = true, viewerId?: string) {
  const query = useMemo(() => adminUsersSummaryQuery(enabled, viewerId), [enabled, viewerId]);
  return useQuery({
    ...query,
    retry: false,
    staleTime: 30_000,
  });
}

export function useAdminUserRoleAssignments(userId: string | null) {
  const { user, permissions } = useAuth();
  const accessScope = user
    ? `${user.id}:${user.systemRole}:${JSON.stringify(permissions ?? [])}`
    : '';
  const query = useMemo(
    () =>
      adminUserRoleAssignmentsQuery(
        userId ?? '00000000-0000-4000-8000-000000000000',
        userId !== null
      ),
    [userId]
  );
  return useQuery({
    ...query,
    queryKey: [...query.queryKey, accessScope],
    enabled: query.enabled && Boolean(accessScope),
    gcTime: 0,
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  const requestIds = useRef(new Map<string, string>());
  return useMutation({
    retry: false,
    mutationFn: (userId: string) => {
      const requestId = requestIds.current.get(userId) ?? crypto.randomUUID();
      requestIds.current.set(userId, requestId);
      return api.delete(`/api/v1/admin/users/${userId}`, {
        schema: adminUserDeletionReceiptSchema,
        headers: { 'Idempotency-Key': requestId },
      });
    },
    onSuccess: (_receipt, userId) => {
      requestIds.current.delete(userId);
      clearQueryResponseCache();
      void queryClient.invalidateQueries({ queryKey: adminUsersKeys.all });
    },
  });
}

export function useAdminUserDeletionStatus(operationId: string | null, accessScope: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [...adminUsersKeys.all, 'deletion', operationId, accessScope],
    enabled: operationId !== null && Boolean(accessScope),
    queryFn: async () => {
      if (operationId === null) throw new Error('No deletion operation selected.');
      const result = await api.get(`/api/v1/admin/user-deletions/${operationId}`, {
        schema: adminUserDeletionStatusSchema,
      });
      if (result.status === 'completed') {
        clearQueryResponseCache();
        void queryClient.invalidateQueries({
          queryKey: adminUsersKeys.all,
          predicate: (query) => query.queryKey[1] !== 'deletion',
        });
      }
      return result;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'blocked' || status === 'failed' ? false : 5_000;
    },
    retry: false,
    gcTime: 0,
  });
}

export function useUpdateAdminUserRoleAssignments(userId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<
    z.infer<typeof updateAdminUserRoleAssignmentsResponseSchema>,
    Error,
    UpdateAdminUserRoleAssignmentsInput
  >({
    // A lost response can follow a committed permission change. Never resend automatically.
    retry: false,
    mutationFn: (input) =>
      api.patch(
        `/api/v1/admin/users/${userId ?? '00000000-0000-4000-8000-000000000000'}/role-assignments`,
        input,
        { schema: updateAdminUserRoleAssignmentsResponseSchema }
      ),
    onSuccess: async () => {
      clearQueryResponseCache();
      await refreshPermissionsAfterRoleSave(queryClient, userId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminUsersKeys.all }),
        queryClient.invalidateQueries({ queryKey: userProfileKeys.all }),
      ]);
    },
  });
}
